import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';
import { useSelector } from 'react-redux';
import {
  selectGroupGuid,
  selectUserGuid,
} from '../../../slices/commonSelectors';
import { toast } from 'react-toastify';
import { camelToSnakeCase } from '../../../utils/caseConversion';
import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import { useRecordIcs201Version } from 'components/IAP/planningView/widgets/ics201VersionRecordingContext';
import { useActualIncidentId } from './commonHooks';

export type ResourceThreatChecklists = Record<
  string,
  OrgEventChecklistItemDef[]
>;

export function resourceSetToApiBody<T extends Record<string, unknown>>(
  data: T
) {
  const {
    members: _members,
    threatChecklists,
    automationRuleChecklists,
    automationRules,
    linkedIcs213rr,
    ...rest
  } = data;
  const snakeCaseData = camelToSnakeCase(rest);
  if (linkedIcs213rr !== undefined) {
    snakeCaseData.linked_ics213rr = linkedIcs213rr;
  }
  if (threatChecklists !== undefined) {
    snakeCaseData.threat_checklists = threatChecklists;
  }
  if (automationRuleChecklists !== undefined) {
    snakeCaseData.automation_rule_checklists = automationRuleChecklists;
  }
  if (automationRules !== undefined) {
    snakeCaseData.automation_rules = automationRules;
  }
  return snakeCaseData;
}

export function remapMangledThreatChecklistKeys(
  checklists: ResourceThreatChecklists | undefined,
  threats: string[] | undefined
): ResourceThreatChecklists {
  const next: ResourceThreatChecklists = { ...(checklists ?? {}) };
  for (const threat of threats ?? []) {
    if (Array.isArray(next[threat]) && next[threat].length > 0) continue;
    const mangled = threat.replace(
      /[A-Z]/g,
      (letter) => `_${letter.toLowerCase()}`
    );
    if (mangled !== threat && Array.isArray(next[mangled])) {
      next[threat] = next[mangled];
    }
  }
  return next;
}

export type LinkedIcs213rrItem = {
  name?: string | null;
  description?: string | null;
  kind?: string | null;
  type?: string | null;
  unit?: string | null;
  quantity?: number | null;
  priority?: string | null;
  resource_set_id?: string | null;
  owning_organization?: string | null;
  requested_reporting_location?: string | null;
  requested_reporting_location_id?: string | null;
  requested_reporting_location_geolocation?: {
    lat: number;
    lng: number;
  } | null;
  cost?: number | null;
  attached_to_asset?: boolean | null;
};

/**
 * Prefilled, never-signed 213RR content saved on an asset. "Create ICS213RR" in
 * an incident turns it into a full draft 213RR scoped to that incident.
 */
export type LinkedIcs213rr = {
  name?: string | null;
  items?: LinkedIcs213rrItem[];
  supply_sources?: string | null;
  substitutes?: string | null;
  capability_enabled?: string | null;
  attachments?: unknown[];
};

export type ResourceSet = {
  id: UUID;
  resource_set_id: UUID;
  resource_site_id: UUID;
  group_guid: UUID;
  name: string;
  description: string;
  capabilities?: string | null;
  geolocation: any;
  kind: string;
  type: string;
  quantity: number;
  status: string;
  consolidation_target_id: UUID;
  archived: boolean;
  date_time_ordered: string; // As a string in ISO format
  datetime_ordered_offset?: number;
  eta: string; // As a string in ISO format
  eta_timezone_offset?: number;
  incidents: UUID[];
  incident_details?: {
    id: UUID;
    name: string;
  }[]; // Optional detailed info about incidents
  current_status?: string; // From ics_resource_in_incident table
  asset_status?: string;
  asset_status_timestamp?: string;
  source_location?: any;
  source_resource_site_id?: UUID;
  destination_location?: any;
  destination_resource_site_id?: UUID;
  in_request?: boolean;
  point_of_contact?: any;
  aor?: any;
  opcon?: any;
  tacon?: any;
  persons_on_board?: any;
  hull_tail_number?: string;
  unit?: string;
  owning_organization?: string;
  address?: string;
  symbology?: string;
  resource_in_incident_details?: {
    id: string;
    incident_id: string;
    assignees: {
      id: string;
      name: string;
      email: string;
    }[];
  }[];
  notes?: string;
  threats?: string[];
  threat_checklists?: ResourceThreatChecklists;
  automation_rules?: string[];
  automation_rule_checklists?: ResourceThreatChecklists;
  geometry?: {
    type: string;
    coordinates: [number, number];
  } | null;
  arcgis_location_link?: {
    external_layer_id: string;
    object_id: number;
  } | null;
  image_url?: string | null;
  document_folders?: {
    name: string;
    notefolderId?: string;
    files: { fileId: string; label: string }[];
  }[];
  linked_ics213rr?: LinkedIcs213rr | null;
  cost?: number | string | null;
  cost_per_time?: number | string | null;
  cost_time_unit?: 'hour' | 'day' | null;
};

type ResourceSetPagination = {
  name?: string[];
  kind?: string[];
  aor?: string[];
  incident?: string[];
  workAvailability?: string[];
  requestStatus?: string[];
  assetStatus?: string[];
  /** Leave out assets whose `notes` start with any of these. */
  excludeNotesPrefix?: string[];
  resultsPerPage?: number;
  pageNumber?: number;
};

export type ResourceSetFilterSelection = {
  name: string[];
  kind: string[];
  aor: string[];
  incident: string[];
  workAvailability: string[];
  requestStatus: string[];
  assetStatus: string[];
  resultsPerPage: number;
  pageNumber: number;
};

export type PaginatedResourceSetsResult = {
  totalItems: number;
  totalPages: number;
  pageNumber: number;
  resultsPerPage: number;
  rows: ResourceSet[];
};

export const keys = {
  base: 'resource_sets',
  get all() {
    return [keys.base];
  },
  list: (
    groupGuid: UUID,
    userGuid: UUID,
    pagination?: ResourceSetPagination
  ) => [keys.base, groupGuid, userGuid, pagination ?? null],
  usage: (resourceSetId: UUID) => [keys.base, 'usage', resourceSetId],
} as const;

export const useResourceSets = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  return useQuery({
    queryKey: keys.list(group_guid, user_guid),
    queryFn: async () => {
      const resp = await axios.get<ResourceSet[]>(
        `${config.apiGateway.ics_resource_sets}/${group_guid}`
      );
      return resp.data;
    },
  });
};

export const usePaginatedResourceSets = (pagination: ResourceSetPagination) => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);

  return useQuery({
    queryKey: keys.list(group_guid, user_guid, pagination),
    queryFn: async () => {
      const toSnakeCase = (key: string) =>
        key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

      const searchParams = new URLSearchParams();
      Object.entries(pagination).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        const snakeKey = toSnakeCase(key);
        if (Array.isArray(value)) {
          if (value.length === 0) return;
          value.forEach((v) => searchParams.append(snakeKey, v));
        } else {
          searchParams.append(snakeKey, String(value));
        }
      });

      const resp = await axios.get<PaginatedResourceSetsResult>(
        `${config.apiGateway.ics_resource_sets_paginated}/${group_guid}`,
        { params: searchParams }
      );
      return resp.data;
    },
  });
};

// CamelCase version for frontend form data
export type CreateResourceSetFormData = {
  name: string;
  description: string;
  capabilities?: string | null;
  geolocation: any;
  resourceSiteId?: UUID | null;
  kind: string;
  type: string;
  assetStatus?: string;
  quantity: number;
  status: string;
  consolidationTargetId: UUID;
  archived: boolean;
  dateTimeOrdered: string;
  datetimeOrderedOffset?: number;
  eta: string;
  etaTimezoneOffset?: number;
  notes?: string;
  threats?: string[];
  threatChecklists?: ResourceThreatChecklists;
  automationRules?: string[];
  automationRuleChecklists?: ResourceThreatChecklists;
  incidents: UUID[];
  members?: UUID[]; // For UI form handling, not sent to backend
  sourceLocation?: any;
  sourceResourceSiteId?: UUID | null;
  destinationLocation?: any;
  destinationResourceSiteId?: UUID | null;
  symbology?: string;
  aor?: string;
  geometry?: {
    type: string;
    coordinates: [number, number];
  } | null;
  arcgisLocationLink?: {
    external_layer_id: string;
    object_id: number;
  } | null;
  imageUrl?: string | null;
  documentFolders?: {
    name: string;
    notefolderId?: string;
    files: { fileId: string; label: string }[];
  }[];
  linkedIcs213rr?: LinkedIcs213rr | null;
  cost?: number | null;
  costPerTime?: number | null;
  costTimeUnit?: 'hour' | 'day' | null;
};

export const useCreateResourceSet = () => {
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  const recordIcs201Version = useRecordIcs201Version();
  return useMutation({
    mutationFn: async (data: CreateResourceSetFormData) => {
      // Convert camelCase form data to snake_case for backend
      // Exclude members field as it's handled separately
      const snakeCaseData = resourceSetToApiBody(
        data as unknown as Record<string, unknown>
      );
      return await axios.post(`${config.apiGateway.ics_resource_sets}`, {
        ...snakeCaseData,
        group_guid,
        user_guid,
        incident_id: incidentId ?? null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(group_guid, user_guid),
      });
      recordIcs201Version();
      toast.success('Resource created successfully');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// CamelCase version for frontend form data
export type EditResourceSetFormData = {
  id?: UUID;
  resourceSetId: UUID;
  resourceSiteId?: UUID | null;
  groupGuid?: UUID;
  name: string;
  description: string;
  capabilities?: string | null;
  geolocation: any;
  kind: string;
  type: string;
  assetStatus?: string;
  quantity: number;
  status: string;
  consolidationTargetId: UUID;
  archived: boolean;
  dateTimeOrdered: string;
  datetimeOrderedOffset?: number;
  eta: string;
  etaTimezoneOffset?: number;
  notes?: string;
  threats?: string[];
  threatChecklists?: ResourceThreatChecklists;
  automationRules?: string[];
  automationRuleChecklists?: ResourceThreatChecklists;
  owningOrganization?: string;
  unit?: string;
  address?: string;
  hullTailNumber?: string;
  pointOfContact?: string;
  opcon?: string;
  tacon?: string;
  incidents: UUID[];
  members?: UUID[]; // For UI form handling, not sent to backend
  sourceLocation?: any;
  sourceResourceSiteId?: UUID | null;
  destinationLocation?: any;
  destinationResourceSiteId?: UUID | null;
  symbology?: string;
  aor?: string;
  /** Form-only; mapped to Point geometry on submit. */
  latitude?: string;
  longitude?: string;
  geometry?: {
    type: string;
    coordinates: [number, number];
  } | null;
  arcgisLocationLink?: {
    external_layer_id: string;
    object_id: number;
  } | null;
  imageUrl?: string | null;
  documentFolders?: {
    name: string;
    notefolderId?: string;
    files: { fileId: string; label: string }[];
  }[];
  linkedIcs213rr?: LinkedIcs213rr | null;
  cost?: number | null;
  costPerTime?: number | null;
  costTimeUnit?: 'hour' | 'day' | null;
};

function moneyFromStored(
  value: number | string | null | undefined
): number | null {
  if (value == null || value === '') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * Builds the base payload for `useUpdateResourceSet` from the resource set as
 * it exists today.
 *
 * The backend writes a new version row from whatever it is given, so an update
 * has to carry every field it means to keep. Callers spread this and then
 * override only what they are changing. Fields are listed explicitly because
 * the read shape carries extras (resolved incident details, assignees, current
 * status) that must not be sent back.
 */
export const mapResourceSetToEditPayload = (
  resourceSetId: UUID,
  existingResourceSet?: ResourceSet
): Partial<EditResourceSetFormData> => {
  if (!existingResourceSet) {
    return {};
  }

  // GET returns PostGIS EWKB hex in `geometry`; `geolocation` holds GeoJSON.
  // updateResourceSet uses ST_GeomFromGeoJSON(geometry), so never send EWKB.
  const geoJsonGeometry = (() => {
    const geom = existingResourceSet.geometry as unknown;
    if (
      geom &&
      typeof geom === 'object' &&
      'type' in (geom as object) &&
      'coordinates' in (geom as object)
    ) {
      return existingResourceSet.geometry ?? null;
    }
    const loc = existingResourceSet.geolocation as unknown;
    if (
      loc &&
      typeof loc === 'object' &&
      'type' in (loc as object) &&
      'coordinates' in (loc as object)
    ) {
      return loc as ResourceSet['geometry'];
    }
    return null;
  })();

  return {
    id: existingResourceSet.id,
    resourceSetId,
    resourceSiteId: existingResourceSet.resource_site_id,
    groupGuid: existingResourceSet.group_guid,
    name: existingResourceSet.name,
    description: existingResourceSet.description,
    capabilities: existingResourceSet.capabilities ?? null,
    geolocation: existingResourceSet.geolocation,
    kind: existingResourceSet.kind,
    type: existingResourceSet.type,
    assetStatus: existingResourceSet.asset_status,
    quantity: existingResourceSet.quantity,
    status: existingResourceSet.status,
    consolidationTargetId: existingResourceSet.consolidation_target_id,
    archived: existingResourceSet.archived,
    dateTimeOrdered: existingResourceSet.date_time_ordered,
    datetimeOrderedOffset: existingResourceSet.datetime_ordered_offset,
    eta: existingResourceSet.eta,
    incidents: existingResourceSet.incidents,
    sourceLocation: existingResourceSet.source_location,
    sourceResourceSiteId: existingResourceSet.source_resource_site_id,
    destinationLocation: existingResourceSet.destination_location,
    destinationResourceSiteId: existingResourceSet.destination_resource_site_id,
    notes: existingResourceSet.notes,
    threats: existingResourceSet.threats ?? [],
    threatChecklists: remapMangledThreatChecklistKeys(
      existingResourceSet.threat_checklists,
      existingResourceSet.threats
    ),
    automationRules: existingResourceSet.automation_rules ?? [],
    automationRuleChecklists: remapMangledThreatChecklistKeys(
      existingResourceSet.automation_rule_checklists,
      existingResourceSet.automation_rules
    ),
    owningOrganization: existingResourceSet.owning_organization,
    unit: existingResourceSet.unit,
    address: existingResourceSet.address,
    hullTailNumber: existingResourceSet.hull_tail_number,
    pointOfContact: existingResourceSet.point_of_contact || undefined,
    opcon: existingResourceSet.opcon || undefined,
    tacon: existingResourceSet.tacon || undefined,
    symbology: existingResourceSet.symbology,
    aor: existingResourceSet.aor || undefined,
    geometry: geoJsonGeometry,
    arcgisLocationLink: existingResourceSet.arcgis_location_link ?? null,
    imageUrl: existingResourceSet.image_url ?? null,
    documentFolders: existingResourceSet.document_folders,
    linkedIcs213rr: existingResourceSet.linked_ics213rr ?? null,
    cost: moneyFromStored(existingResourceSet.cost),
    costPerTime: moneyFromStored(existingResourceSet.cost_per_time),
    costTimeUnit:
      existingResourceSet.cost_time_unit === 'hour' ||
      existingResourceSet.cost_time_unit === 'day'
        ? existingResourceSet.cost_time_unit
        : null,
  };
};

export const useUpdateResourceSet = (resourceSetId: UUID) => {
  const user_guid = useSelector(selectUserGuid);
  const group_guid = useSelector(selectGroupGuid);
  const incidentId = useActualIncidentId();
  const queryClient = useQueryClient();
  const recordIcs201Version = useRecordIcs201Version();
  return useMutation({
    mutationFn: async (data: Partial<EditResourceSetFormData>) => {
      // Convert camelCase form data to snake_case for backend
      // Exclude members field as it's handled separately
      const snakeCaseData = resourceSetToApiBody(
        data as unknown as Record<string, unknown>
      );
      const id = data.resourceSetId || resourceSetId;
      return await axios.put<ResourceSet>(
        `${config.apiGateway.ics_resource_sets}/${id}`,
        {
          ...snakeCaseData,
          user_guid,
          group_guid,
          incident_id: incidentId ?? null,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
      recordIcs201Version();
      toast.success('Resource updated successfully');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export type ArchiveResourceSetsResult = {
  archived: number;
  already_archived: number;
  not_found: number;
};

/** Soft-delete assets: the server saves a new, archived version of each. */
export const useArchiveResourceSets = () => {
  const group_guid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (resourceSetIds: string[]) => {
      const resp = await axios.post<ArchiveResourceSetsResult>(
        config.apiGateway.ics_resource_sets_archive,
        { group_guid, resource_set_ids: resourceSetIds }
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
};

export const useFetchResourceSetUsage = (resourceSetId: string) => {
  const group_guid = useSelector(selectGroupGuid);

  return useQuery({
    queryKey: keys.usage(resourceSetId),
    queryFn: async () => {
      try {
        const resp = await axios.get(
          `${config.apiGateway.ics_resource_sets}/usage/${resourceSetId}`,
          {
            params: {
              group_guid: group_guid,
            },
          }
        );
        return resp.data;
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';
        toast.error(errorMessage);
      }
    },
  });
};

// Define types for the mutation function parameters
type SplitResourceSetParams = {
  resource_set_id: UUID;
  quantity: number;
  name: string;
  resource_site_id: UUID;
};

type JoinResourceSetsParams = {
  source_id: UUID;
  target_id: UUID;
};

export const useSplitResourceSet = () => {
  const queryClient = useQueryClient();
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({
      resource_set_id,
      quantity,
      name,
      resource_site_id,
    }: SplitResourceSetParams) => {
      return await axios.post(
        `${config.apiGateway.ics_resource_sets}/${resource_set_id}/split`,
        {
          quantity,
          name,
          resource_site_id,
          user_guid,
          group_guid,
          incident_id: incidentId ?? null,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(group_guid, user_guid),
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useJoinResourceSets = () => {
  const queryClient = useQueryClient();
  const group_guid = useSelector(selectGroupGuid);
  const user_guid = useSelector(selectUserGuid);
  const incidentId = useActualIncidentId();

  return useMutation({
    mutationFn: async ({ source_id, target_id }: JoinResourceSetsParams) => {
      return await axios.post(
        `${config.apiGateway.ics_resource_sets}/${target_id}/join/${source_id}`,
        { user_guid, group_guid, incident_id: incidentId ?? null }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.list(group_guid, user_guid),
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};
